import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Button } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { clsx } from 'clsx';
import dayjs from 'dayjs';

import { DownloadIcon, InfoTooltip, LightTooltip } from '@components';
import { rbac, ROLES } from '@utils';
import { creditHttp, CreditReportTypes, s3http } from '@network';
import { useRequest } from '@hooks';
import { Credit, CreditRisk, CreditRiskState, Doctype, DoctypeTeamCategory } from '@types';
import { DoctypeDocument } from '../DoctypeDocument';
import { CreditReportFiles, CreditReportsType } from '@pages/credit/CreditReport';
import { PdfPortal } from '@components/PdfPortal/PdfPortal';
import cls from './credit-widget.module.css';

interface Props {
  credit: Credit;
  setCredit: Dispatch<SetStateAction<Credit>>;
  doctypes: Doctype[];
  isSuperadmin: boolean;
  editable: boolean;
}

export const CreditWidget = ({ credit, setCredit, doctypes, isSuperadmin, editable }: Props) => {
  const [tab, setTab] = React.useState(0);
  const [salesFilled, setSalesFilled] = React.useState(0);
  const [riskFilled, setRiskFilled] = React.useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [risk, setRisk] = useState<CreditRisk | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasRiskGenerated = useRef<boolean>(false);
  const reportNodeRef = useRef(null);

  const [reportFiles, setReportFiles] = useState<CreditReportsType>({
    [CreditReportTypes.EKATENA_REPORT]: [],
    [CreditReportTypes.BUREAU_REPORT]: [],
    [CreditReportTypes.SINECTA_REPORT]: [],
  });

  const doctypeSales: Record<number, boolean> = {};
  const doctypeRisk: Record<number, boolean> = {};

  for (const doctype of doctypes) {
    if (doctype.teamCategory === DoctypeTeamCategory.SALES) {
      doctypeSales[doctype.id] = true;
    } else if (doctype.teamCategory === DoctypeTeamCategory.RISK) {
      doctypeRisk[doctype.id] = true;
    }
  }

  const hasRestrictedAccess = rbac.hasRestrictedAccess();
  const hasDownloadAccess = rbac.hasDownloadRestrictionRole();
  const isQCCredit = credit.createdByDealerId;

  const changeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);

    credit && (async () => {
      const nextCredit = await creditHttp.getCredit(credit.id);
      setCredit(nextCredit);
    })();
  };

  const updateCounters = async () => {
    const [updatedCredit, loadedFiles] = await Promise.all([
      creditHttp.getCredit(credit.id),
      creditHttp.getReportFiles(credit.id),
    ]);

    const salesDcs = updatedCredit.documents.filter(d =>
      doctypeSales[d.doctypeId] && (d.files.length || d.number || d.isOptionalChecked),
    );
    const salesDcsUniqueIds = new Set(salesDcs.map(i => i.doctypeId));
    setSalesFilled(salesDcsUniqueIds.size);

    const riskDcs = updatedCredit.documents.filter(d =>
      doctypeRisk[d.doctypeId] && (d.files.length || d.number || d.isOptionalChecked),
    );
    const riskDcsUniqueIds = new Set(riskDcs.map(i => i.doctypeId));
    setRiskFilled(riskDcsUniqueIds.size);

    if (loadedFiles) {
      setReportFiles(loadedFiles);
    }
  };

  const filteredDoctypes = doctypes.filter(doctype => {
    if (tab === 0 && doctype.teamCategory === DoctypeTeamCategory.SALES) {
      return true;
    }
    if (tab === 1 && doctype.teamCategory === DoctypeTeamCategory.RISK) {
      return true;
    }
    return false;
  });

  const { submit: downloadDocuments, loading: downloadLoading } = useRequest(async () => {
    await creditHttp.downloadCreditDocuments(credit.id);
  });

  const {
    submit: downloadLoanTapeFiles,
  } = useRequest(async () => {
    setIsLoading(true);
    await getCreditRisk();
  });

  const { submit: compressDocuments, loading: compressLoading } = useRequest(async () => {
    await creditHttp.compressDocuments(credit.id, credit.code, dayjs().format('YYYY-MM-DD_HH-mm-ss'));
  });

  useEffect(() => {
    updateCounters();
  }, [credit, tab, doctypes]);

  useEffect(() => {
    credit?.id && (async () => {
      const nextRisk = await creditHttp.getRisk(credit?.id);
      nextRisk && setRisk(nextRisk);
    })();
  }, [credit?.id]);

  const reportsValidationList = useMemo(() => {
    const requiredReportTypes = [
      !!reportFiles.EKATENA_REPORT.length,
      !!reportFiles.BUREAU_REPORT.length,
      !!reportFiles.SINECTA_REPORT.length,
    ];

    if (credit.risk?.state === CreditRiskState.COMPLETE) {
      requiredReportTypes.push(credit.risk?.state === CreditRiskState.COMPLETE);
    }

    return requiredReportTypes;
  }, [reportFiles]);

  const isRiskTab = tab === 1;
  const reportsUploadedCount = reportsValidationList.filter(Boolean).length;
  const salesCounterTotal = Object.keys(doctypeSales).length;
  const riskCounterTotal = Object.keys(doctypeRisk).length + reportsValidationList.length;

  const getCreditRisk = async () => {
    if (!credit.id || !editable) return;

    setIsGenerating(true);

    try {
      const riskData = await creditHttp.getRisk(credit.id);
      setRisk(riskData);
    } catch (error) {
      console.error('Error:', error);
      setIsGenerating(false);
    }
  };

  const uploadCreditRiskPdf = useCallback(async (blob: Blob) => {
    if (!editable) return;
    try {
      if (hasRiskGenerated.current) return;

      hasRiskGenerated.current = true;

      const file = new File([blob], `${credit.code}_risk_report.pdf`, {
        type: blob.type,
        lastModified: Date.now(),
      });

      const reportFile = await s3http.uploadFileToUrl(file, `/credit-risk/${credit.id}/upload-risk-report`);

      await creditHttp.downloadLoanTapeFiles(credit.id, reportFile.id);
      setIsLoading(false);
    } catch (error) {
      console.error('Error sending PDF:', error);
      setIsLoading(false);
    } finally {
      setIsGenerating(false);
      setRisk(null);
      hasRiskGenerated.current = false;
    }
  }, [credit.id, credit.code]);

  return (
    <div className={cls.container} id="credit-docs">
      <div className={cls.header}>
        <Tabs value={tab} onChange={changeTab} className={cls.tabs}>
          <Tab label={(
            <div className={cls.tabLabel}>
              {DoctypeTeamCategory.SALES}
              <span className={cls.tabCounter}>{salesFilled}/{salesCounterTotal}</span>
            </div>
          )} {...tabProps(0, tab)} />
          <Tab label={(
            <div className={cls.tabLabel}>
              {DoctypeTeamCategory.RISK}
              <span className={cls.tabCounter}>
                {riskFilled + reportsUploadedCount}/{riskCounterTotal}
              </span>
            </div>
          )} {...tabProps(1, tab)} />
        </Tabs>

        {hasDownloadAccess ? (
          <div className={cls.actions}>
            <Button
              variant="outlined"
              color="primary"
              onClick={downloadLoanTapeFiles}
              disabled={isLoading}
              className={cls.downloadBtn}
            >
              <div className={cls.downloadBtnIcon}>
                {isLoading
                  ? <CircularProgress color="inherit" size={20} />
                  : <DownloadIcon />
                }
              </div>
              Download Loan Tape File
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={downloadDocuments}
              disabled={downloadLoading}
              className={cls.downloadBtn}>
              <div className={cls.downloadBtnIcon}>
                {downloadLoading
                  ? <CircularProgress color="inherit" size={20} />
                  : <DownloadIcon />}
              </div>
              Download all documents
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={compressDocuments}
              disabled={compressLoading}
              className={cls.downloadBtn}>
              <div className={cls.downloadBtnIcon}>
                {compressLoading
                  ? <CircularProgress color="inherit" size={20} />
                  : <DownloadIcon />}
              </div>
              Compress
            </Button>
          </div>
        ) : (
          <LightTooltip title={`Requires role: ${ROLES.DOWNLOAD_RESTRICTION}`}>
            <div className={cls.downloadBtn}>
              <InfoTooltip lockIcon title="" />
              Download all documents
            </div>
          </LightTooltip>
        )}
      </div>

      <div className={cls.documents}>
        {filteredDoctypes.map(doctype => (
          <div className={cls.document} key={doctype.id}>
            <DoctypeDocument
              doctype={doctype}
              credit={credit}
              setCredit={setCredit}
              updateCounters={updateCounters}
              isSuperadmin={isSuperadmin}
              downloadable={rbac.hasDownloadRestrictionRole()}
              uploadable={rbac.hasUploadPermissionRole()}
              editable={isQCCredit ? false : isRiskTab ? hasRestrictedAccess : editable}
              isRestricted={doctype.isRestricted && !hasRestrictedAccess}
            />
          </div>
        ))}
        {isRiskTab && (
          <div className={cls.reports}>
            <CreditReportFiles
              creditId={credit.id}
              files={reportFiles}
              setReportFiles={setReportFiles}
              updateCounters={updateCounters}
              isRestricted={!hasRestrictedAccess}
              hideStatus
              risk={credit.risk}
              reportNodeRef={reportNodeRef}
              editable={editable}
            />
          </div>
        )}
      </div>

      {risk && (
        <PdfPortal
          risk={risk}
          onPdfGenerated={uploadCreditRiskPdf}
          isActive={isGenerating}
        />
      )}
    </div>
  );
};

function tabProps(index: number, selectedIndex: number) {
  return {
    id: `credit-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
    className: clsx(cls.tab, index === selectedIndex && cls.tabSelected),
  };
}
